import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Modal, Input, PasswordInput, Select, Button } from "../../../components/ui";
import { ADD_COUNTRY_ADMIN, GET_COUNTRY, UPDATE_COUNTRY_ADMIN } from "../../../api/url";
import { useAppMutation, useAppQuery } from "../../../api/fetchClient";
import { QUERY_KEYS } from "../../../api/queryKeys";
import { toast } from "react-toastify";
import { strings } from "../../../strings/strings";

const AddCountryAdminModal = ({ isOpen, onClose, onSuccess, initialData, isEdit }) => {
  const addCountryAdminMutation = useAppMutation();
  const [countries, setCountries] = useState([]);

  const { data: countriesData } = useAppQuery({
    queryKey: QUERY_KEYS.COUNTRIES,
    url: GET_COUNTRY,
    method: "GET",
    enabled: isOpen,
  });
  useEffect(() => {
    if (countriesData?.results) {
      const countriesList = Array.isArray(countriesData.results)
        ? countriesData.results
        : countriesData.results.data || [];

      const onlineCountries = (countriesList || []).filter((country) => {
        if (!country || typeof country !== "object") return false;
   
        return country.isOnline === true || country.is_online === true;
      });

      const mappedCountries = onlineCountries.map((country) => {
        return country.name || country.country_name || country.countryName || "";
      }).filter(Boolean);
      
      setCountries(mappedCountries);
    } else {
      setCountries([]);
    }
  }, [countriesData]);


  const validationSchema = Yup.object({
    countryName: Yup.string().required(strings.countrynamerequired),
    username: Yup.string().required(strings.usernameisrequired),
    email: Yup.string().email(strings.invalidemail).required(strings.emailRequired),
    password: Yup.string().when("isEdit", {
      is: false,
      then: (schema) => schema.required(strings.passwordisrequired),
      otherwise: (schema) => schema.optional(),
    }),
    confirmPassword: Yup.string().when("isEdit", {
      is: false,
      then: (schema) =>
        schema
          .oneOf([Yup.ref("password"), null], strings.passwordmismatch)
          .required(strings.confirmpasswordisrequired),
      otherwise: (schema) => schema.optional(),
    }),
  });

  const formik = useFormik({
    initialValues: {
      countryName: initialData?.countryName || "",
      username: initialData?.username || "",
      email: initialData?.email || "",
      password: "",
      confirmPassword: "",
      isEdit: isEdit,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        let payload = {};

        if (isEdit) {
          if (values.username !== initialData.username) payload.username = values.username;
          if (values.email !== initialData.email) payload.email = values.email;
          if (values.countryName !== initialData.countryName) payload.country = values.countryName;
          
          if (Object.keys(payload).length === 0) {
            toast.info("No changes detected");
            onClose();
            return;
          }
        } else {
          payload = {
            username: values.username,
            country: values.countryName,
            email: values.email,
            password: values.password,
            confirmPassword: values.confirmPassword,
          };
        }

        await addCountryAdminMutation.mutateAsync({
          url: isEdit ? `${UPDATE_COUNTRY_ADMIN}?id=${initialData.id}` : ADD_COUNTRY_ADMIN,
          method: isEdit ? "PUT" : "POST",
          data: payload,
        });

        resetForm();
        onClose();
        onSuccess(values);
        toast.success(isEdit ? strings.countryadmintitle + " Updated" : strings.countryuadminadded);
      } catch (err) {
        toast.error(err.message || strings.failedtoaddcountryadmin);
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  const footer = (
    <div className="flex justify-end gap-3 py-2">
      <Button
        variant="outline"
        onClick={handleClose}
        disabled={addCountryAdminMutation.isPending}
      >
        {strings.cancel}
      </Button>

      <Button
        type="submit"
        form="add-country-admin-form"
        disabled={addCountryAdminMutation.isPending}
        isLoading={addCountryAdminMutation.isPending}
      >
        {isEdit ? strings.edit : strings.addcountryadmin}
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEdit ? strings.edit + " " + strings.countryadmintitle : strings.addcountryadmin}
      footer={footer}
    >
      <form id="add-country-admin-form" onSubmit={formik.handleSubmit} className="space-y-5">
        <Select
          label={strings.countryName}
          name={strings.countryName2}
          required
          placeholder={strings.selectcountry}
          value={formik.values.countryName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.countryName}
          touched={formik.touched.countryName}
          options={countries}
        />

        <Input
          label="Username"
          name="username"
          type="text"
          required
          placeholder={strings.username}
          value={formik.values.username}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.username}
          touched={formik.touched.username}
        />

        <Input
          label={strings.email}
          name={strings.email2}
          type="email"
          required
          placeholder={strings.email}
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.email}
          touched={formik.touched.email}
        />

        {!isEdit && (
          <>
            <PasswordInput
              label={strings.password}
              name="password"
              required
              placeholder={strings.password}
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.errors.password}
              touched={formik.touched.password}
            />
            <PasswordInput
              label={strings.confirmpassword}
              name="confirmPassword"
              required
              placeholder={strings.confirmpassword}
              value={formik.values.confirmPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.errors.confirmPassword}
              touched={formik.touched.confirmPassword}
            />
          </>
        )}
      </form>
    </Modal>
  );
};

export default AddCountryAdminModal;
